import { DateTime } from 'luxon';

import { COMMERCIAL_POLICY_ICONS } from '@amfam/policy/models/lib/models/commercial-policy-type.enum';
import { PolicyTypeDisplayNameConstants } from '@amfam/policy/models/lib/models/policy-type-display-name-constants';
import { PolicyTypeIconConstants } from '@amfam/policy/models/lib/models/policy-type-icon-constants';
import { VehicleTypeConstants } from '@amfam/policy/models/lib/models/vehicle-type-constants';

import { Policy } from './policy';
import { PolicyBase } from './policy-base';
import { PolicyMigrationBadge } from './policy-migration';
import { PolicyStatus } from './policy-status';
import { UserAPIRiskSummary } from './user-api-risk-summary';

export class PolicySummary extends PolicyBase {
  // Properties inherited from PolicyBase: policyNumber, productType, billingAccountNumber, policyPartnerId, platform
  public effectiveDate: string;
  public endEffectiveDate: string;
  public cancelDate: string;
  public billAccountNumber: string;
  public status: string;
  public policyStatus: PolicyStatus;
  public servicingProducerId: string;
  public policyTypeCode: string;
  public contractState: string;
  public sortType?: string;
  public operator: boolean;
  public poiLink?: string;
  public eoiLink?: string;
  public summaryRisks: UserAPIRiskSummary[] = new Array<UserAPIRiskSummary>();
  public iconType = 'icon-auto';
  public policyDetailsRoute: string;
  public amfamCompanyCode?: string;
  public amfamCompanyDescription?: string;
  public isAfi: boolean;
  public afiMidvaleBillingAccountNumbers: string[] = [];
  public phoenixHorisonBillingAccountNumbers: string[] = [];
  public isCommercial: boolean;
  public productTypeCode: string | undefined;
  public migrationBadge?: PolicyMigrationBadge;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(json: any) {
    super(json);
    PolicyBase.newAssignProperties(this, {
      ...json,
      policyNumber: json.sourceSystemProductIdentifier,
      sourceSystem: json.sourceSystemName
    });
    this.productTypeCode = json.productTypeCode;
    this.productDescription = PolicySummary.mapPolicyTypeCategory(json.policyTypeCategory);
    this.effectiveDate = json.policyEffectiveDate;
    this.endEffectiveDate = json.policyExpirationDate;
    this.cancelDate = json.cancelDate;
    this.billAccountNumber = json.billingAccountNumber;
    this.status = PolicySummary.getPolicyStatus(
      json.policyInForceIndicator,
      this.effectiveDate,
      json.policyScheduledIndicator
    );
    this.policyStatus = Policy.getPolicyStatusType(this.status);
    this.servicingProducerId = json.servicingAgentProducerId;
    this.policyTypeCode = json.policyTypeCode;
    this.contractState = json.contractState;
    this.operator = json.operator;
    this.policyDetailsRoute = PolicySummary.getPolicyDetailsRoute(this.policyTypeDisplayName ?? '');
    if (json.amfamCompanyCode) {
      this.amfamCompanyCode = json.amfamCompanyCode.code;
      this.amfamCompanyDescription = json.amfamCompanyCode.description;
    }
    this.policyPartnerId = json.policyPartnerId;
    this.platform = json.platform;
    this.isAfi = this.policyPartnerId === 'AFI';
    this.afiMidvaleBillingAccountNumbers = json.afiMidvaleBillingAccountNumbers ?? [];
    this.phoenixHorisonBillingAccountNumbers = json.phoenixHorisonBillingAccountNumbers ?? [];
    this.migrationBadge = json.migrationBadge;
    this.isCommercial = this.policyTypeDisplayName?.toLowerCase() === 'commercial';
    if (this.isCommercial && this.productTypeCode) {
      const iconType =
        COMMERCIAL_POLICY_ICONS[this.productTypeCode as keyof typeof COMMERCIAL_POLICY_ICONS];
      if (iconType) {
        this.iconType = iconType;
      }
    } else
      this.iconType = PolicySummary.getIconType(
        this.policyTypeDisplayName,
        this.productDescription
      );

    if (json.risks) {
      for (const risk of json.risks) {
        this.summaryRisks.push(new UserAPIRiskSummary(risk));
      }
    }
  }

  private static getIconType(
    policyTypeDisplayName: string | undefined,
    productDescription: string
  ): string {
    const displayConstants = PolicyTypeDisplayNameConstants as unknown as Record<string, string>;
    const iconConstants = PolicyTypeIconConstants as unknown as Record<string, string>;
    const key =
      Object.keys(PolicyTypeDisplayNameConstants).find(
        keyValue =>
          String(displayConstants[keyValue]).toUpperCase() === policyTypeDisplayName?.toUpperCase()
      ) ?? '';
    if (String(displayConstants[key]).toUpperCase() === 'AUTO') {
      // when cycle, rv and watercraft
      const icon = iconConstants[productDescription?.toUpperCase().trim().replace(/\s/g, '')];
      if (icon) return icon;
    }

    return iconConstants[key] ?? '';
  }

  private static getPolicyDetailsRoute(policyTypeDisplayName: string): string {
    switch (policyTypeDisplayName.toUpperCase()) {
      case 'AUTO':
        return 'auto';
      case 'HOME':
        return 'property';
      case 'LIFE':
        return 'life';
      case 'UMBRELLA':
        return 'umbrella';
      case 'COMMERCIAL':
        return 'commercial';
    }
    return policyTypeDisplayName;
  }

  private static mapPolicyTypeCategory(policyTypeCategory: string): string {
    switch (policyTypeCategory?.toLowerCase()) {
      case 'private passenger':
        return VehicleTypeConstants.AUTO;
      case 'motorhome':
        return VehicleTypeConstants.RV;

      default:
        return policyTypeCategory;
    }
  }

  private static getPolicyStatus(
    policyInForceIndicator: boolean | string,
    effectiveDate: string,
    isPolicyScheduled = false
  ): string {
    if (policyInForceIndicator === true || policyInForceIndicator === 'true') {
      return 'Inforce';
    }
    const effectiveDateOnly = DateTime.fromISO(effectiveDate, { zone: 'utc' }).toFormat(
      'yyyy-MM-dd'
    );
    const todayDate = DateTime.now().toFormat('yyyy-MM-dd');
    if (isPolicyScheduled || effectiveDateOnly > todayDate) {
      return 'Scheduled';
    }
    return 'Other';
  }
}
